import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ContentBox, ContentPage, FullTable, NoResults, PageActions, StatePill, Text } from "@ficatranquilo/contabilidade.com-ui";
import { fixedAdjustmentCompetence } from "@helpers/fixedAdjustmentCompetence";
import useService from "@hooks/useService";
import HelpVideoButton from "@components/HelpVideoButton";
import { DomainStatus, formatBRL, formatDate, usePageHeader } from "@components/ow";
import { remunerationSummary } from "../../Common/ContractDetail";
import {
  CompetenceCell,
  Inline,
  InvoiceStatusCell,
  PaymentFilters,
  SCHEDULE_STATUS,
  STATUS_DATA,
  Stack,
  balanceLabel,
} from "../../Common/PaymentSchedules";

// Pagamentos do prestador (modelo Lista do DS OneWork): os próprios cronogramas, só
// leitura. O detalhe explica o valor e recebe a nota.

// Página da API por requisição: uma das opções do seletor da FullTable (10, 25, 50, 100)
const PAGE_SIZE = 25;

const LIST_HREF = "/e/payment-schedules";

const BREADCRUMB = [{ page: "Pagamentos", route: LIST_HREF }];

const EMPTY = { totalRegisters: 0, list: [], totalPages: 1 };

/**
 * Valor esperado na visão do prestador. Na competência em apuração do valor fixo com
 * ajuste, o valor ainda muda a cada aprovação de horas: quem manda é o saldo, que é
 * com o que o prestador se organiza; o dinheiro vira "até agora".
 */
function ValueCell({ schedule }) {
  const competence = fixedAdjustmentCompetence(schedule);
  if (competence?.isOpen) {
    return (
      <Stack $gap="2px">
        <Inline>
          <Text $text={balanceLabel(competence.balanceHours)} $variant="paragraph_small_bold" $color="N900" />
          <StatePill $text="Parcial" $variant="orange" $size="small" />
        </Inline>
        <Text $text={`${formatBRL(competence.partialValue)} até agora`} $variant="caption_medium" $color="N400" />
      </Stack>
    );
  }
  const hasValue = schedule.expected_value != null && schedule.expected_value !== "";
  return (
    <Inline>
      {hasValue ? formatBRL(schedule.expected_value) : "-"}
      {competence?.isFrozen ? <StatePill $text="Encerrada" $variant="gray" $size="small" /> : null}
    </Inline>
  );
}

export default function EmployeePaymentSchedule() {
  const navigate = useNavigate();
  const paymentScheduleService = useService("payment-schedules");
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [paginationData, setPaginationData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState(STATUS_DATA[0]);
  // Competência { month, year }; null = todas
  const [filterCompetence, setFilterCompetence] = useState(null);
  // Só a resposta da última requisição vale (filtros seguidos podem voltar fora de ordem)
  const lastRequest = useRef(0);

  usePageHeader({ title: "Pagamentos", breadcrumb: BREADCRUMB });

  const statusValue = filterStatus.value;
  const hasFilter = !!statusValue || !!filterCompetence;

  useEffect(() => {
    fetchSchedules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageIndex, pagination.pageSize, statusValue, filterCompetence]);

  async function fetchSchedules() {
    const request = ++lastRequest.current;
    setLoading(true);
    try {
      // Sem scope=company o back devolve só os cronogramas de quem chama
      const params = [`page=${pagination.pageIndex + 1}`, `limit=${pagination.pageSize}`];
      if (statusValue) params.push(`status=${statusValue}`);
      if (filterCompetence) {
        params.push(`reference_month=${filterCompetence.month}`, `reference_year=${filterCompetence.year}`);
      }
      const result = await paymentScheduleService.list(params.join("&"));
      if (request !== lastRequest.current) return;

      if (result && result.data && result.data.list) {
        setPaginationData(result.data);
      } else {
        throw new Error("Erro ao carregar cronogramas de pagamento");
      }
    } catch (error) {
      if (request !== lastRequest.current) return;
      console.error(error);
      toast.error(error?.response?.data?.msg || error?.message || "Erro ao carregar cronogramas de pagamento");
      setPaginationData(EMPTY);
    }
    setLoading(false);
  }

  const resetPage = () => setPagination((prev) => ({ ...prev, pageIndex: 0 }));

  function changeStatus(option) {
    setFilterStatus(option);
    resetPage();
  }

  function changeCompetence(competence) {
    setFilterCompetence(competence);
    resetPage();
  }

  function clearFilters() {
    setFilterStatus(STATUS_DATA[0]);
    setFilterCompetence(null);
    resetPage();
  }

  const detailsRoute = (schedule) => `${LIST_HREF}/${schedule.id}`;

  const columns = [
    { id: "competence", header: "Competência", size: 200, cell: ({ row }) => <CompetenceCell schedule={row.original} /> },
    {
      id: "remuneration",
      header: "Remuneração",
      size: 220,
      cell: ({ row }) => {
        // A API responde com "contract" (minúsculo); "Contract" fica como fallback
        const contract = row.original.contract || row.original.Contract;
        return contract ? remunerationSummary(contract) : "-";
      },
    },
    { id: "due_date", header: "Vencimento", size: 110, cell: ({ row }) => (row.original.due_date ? formatDate(row.original.due_date) : "-") },
    { id: "expected_value", header: "Valor esperado", size: 200, cell: ({ row }) => <ValueCell schedule={row.original} /> },
    {
      id: "actual_value",
      header: "Valor pago",
      size: 120,
      cell: ({ row }) => (Number(row.original.actual_value) ? formatBRL(row.original.actual_value) : "-"),
    },
    {
      id: "status",
      header: "Status",
      size: 110,
      cell: ({ row }) => <DomainStatus domain="pagamento" value={SCHEDULE_STATUS[row.original.status] || row.original.status} />,
    },
    { id: "invoice", header: "Nota fiscal", size: 120, cell: ({ row }) => <InvoiceStatusCell schedule={row.original} /> },
  ];

  const noResults = hasFilter ? (
    <NoResults notResultText="Nenhum pagamento encontrado" includeText="Limpar filtros" includeAction={clearFilters} />
  ) : (
    <NoResults notResultText="Nenhum pagamento por aqui" />
  );

  return (
    <ContentPage>
      <PageActions>
        <HelpVideoButton videoKey="employeePaymentSchedule" />
      </PageActions>

      <ContentBox>
        <Stack $gap="24px">
          <PaymentFilters
            id="employee-payment-schedules-competence"
            status={filterStatus}
            onStatusChange={changeStatus}
            competence={filterCompetence}
            onCompetenceChange={changeCompetence}
            onClear={clearFilters}
          />

          <FullTable
            data={paginationData.list}
            columns={columns}
            rowCount={paginationData.totalRegisters}
            pagination={pagination}
            setPagination={setPagination}
            getRowId={(row) => String(row.id)}
            isLoading={loading}
            onRowClick={(schedule) => navigate(detailsRoute(schedule))}
            noResults={noResults}
          />
        </Stack>
      </ContentBox>
    </ContentPage>
  );
}
